import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import * as XLSX from 'xlsx';
import endpoints from '../../config/endpoints';
import { setLoading } from '../../services/reducers/homeSlice';
import restApi from '../../services/restApi';
import tools from '../../utils/tools';
import AnalyticsLayout, { DATE_ALL, DateRange, toToman } from './AnalyticsLayout';

// Brand palette only — blue / sky blue / gray / near-black (no rainbow).
const COLORS = ['#1A6BFF','#4D8FFF','#7A8CA0','#3D4F6B','#0F1923','#1254D6','#93C5FD','#64748B'];
const LEAST_SOLD_COLOR = '#94A3B8';
const TOP_N = 40;

export default function Products() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>({ from: DATE_ALL, to: DATE_ALL });
  const [listData, setListData] = useState<any>(null);
  const [search, setSearch] = useState('');
  const [unitFilter, setUnitFilter] = useState<'all' | 'box' | 'roll'>('all');

  const fetchList = useCallback(async () => {
    dispatch(setLoading(true));
    const apiQuery = dateRange.from && dateRange.to ? { from: dateRange.from, to: dateRange.to } : {};
    const res = await restApi(endpoints.analytics.products).get(apiQuery);
    if (res.code === 200) setListData(res.data);
    dispatch(setLoading(false));
  }, [dateRange, dispatch]);

  // Presets apply immediately (no separate "apply" button) — refetch
  // whenever the resolved date range changes.
  useEffect(() => { fetchList(); }, [fetchList]);

  const allProducts: any[] = listData?.topProducts || [];

  // Restrict every per-product breakdown (charts + table) to one packaging
  // type at a time — box and roll counts aren't comparable side by side.
  const unitFiltered = allProducts.filter(p => unitFilter === 'all' || p.unitType === unitFilter);

  const filtered = unitFiltered.filter(p => {
    const q = search.toLowerCase();
    return !q || (p.title || '').toLowerCase().includes(q) || (p.sku || '').toLowerCase().includes(q);
  });

  const monthlyProducts: any[] = listData?.monthlyProducts || [];

  const top10 = unitFiltered.slice(0, TOP_N).map(p => ({
    name: p.sku || p.title,
    count: p.totalCount,
    revenue: p.totalRevenue,
  }));

  // Backend already returns every sold product sorted by count DESC, so the
  // least-selling view is just the same list re-sorted ascending — no
  // backend change needed. Products with literally zero sales don't appear
  // here at all (they were never in an OrderProduct row to begin with); see
  // the customer-facing "کالا های هرگز خریداری نشده" feature for that case.
  const leastSold = [...unitFiltered]
    .sort((a, b) => a.totalCount - b.totalCount)
    .slice(0, 20)
    .map(p => ({
      name: p.sku || p.title,
      count: p.totalCount,
      revenue: p.totalRevenue,
    }));

  const exportProducts = () => {
    const rows = filtered.map((p, i) => ({
      '#': i + 1,
      'عنوان': p.title,
      'SKU': p.sku || '—',
      'تعداد فروخته‌شده': p.totalCount,
      'تعداد سفارشات': p.orderCount,
      'مجموع درآمد (تومان)': toToman(p.totalRevenue),
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'محصولات');
    XLSX.writeFile(wb, 'products-analytics.xlsx');
  };

  return (
    <AnalyticsLayout tab="products" title="تحلیل محصولات" dateRange={dateRange} onDateChange={setDateRange}>

      {/* Monthly products chart */}
      {monthlyProducts.length > 0 && (
        <div className="analyticsChartGrid" style={{ gridTemplateColumns: '1fr' }}>
          <div className="analyticsChartCard">
            <div className="analyticsChartTitle">تعداد کالاهای فروخته‌شده به تفکیک ماه — عدد (شمسی)</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={monthlyProducts} margin={{ right: 16, left: 8, top: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fontFamily: 'Yekan Bakh' }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip formatter={(v: any) => [v, 'عدد']} labelFormatter={(l: string) => `ماه: ${l}`} />
                <Bar dataKey="count" fill="#1A6BFF" radius={[6, 6, 0, 0]}>
                  {monthlyProducts.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top charts */}
      {top10.length > 0 && (
        <div className="analyticsChartGrid">
          <div className="analyticsChartCard">
            <div className="analyticsChartTitle">پرفروش‌ترین محصولات (عدد)</div>
            <ResponsiveContainer width="100%" height={Math.max(top10.length * 24, 280)}>
              <BarChart data={top10} layout="vertical" margin={{ right: 16, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fontFamily: 'Yekan Bakh', direction: 'ltr' }} />
                <Tooltip formatter={(v: any) => [v, 'عدد']} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]}>
                  {top10.map((_: any, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="analyticsChartCard">
            <div className="analyticsChartTitle">پرفروش‌ترین محصولات (درآمد)</div>
            <ResponsiveContainer width="100%" height={Math.max(top10.length * 24, 280)}>
              <BarChart data={top10} layout="vertical" margin={{ right: 16, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={v => tools.formatPrice(toToman(v))} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fontFamily: 'Yekan Bakh', direction: 'ltr' }} />
                <Tooltip formatter={(v: any) => [tools.formatPrice(toToman(v)) + ' تومان', 'درآمد']} />
                <Bar dataKey="revenue" radius={[0, 6, 6, 0]}>
                  {top10.map((_: any, i: number) => <Cell key={i} fill={COLORS[(i + 4) % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Least-selling — same underlying data, sorted ascending, muted gray to
          visually distinguish "needs attention" from the highlighted best-sellers. */}
      {leastSold.length > 0 && (
        <div className="analyticsChartGrid" style={{ gridTemplateColumns: '1fr' }}>
          <div className="analyticsChartCard">
            <div className="analyticsChartTitle">کم‌فروش‌ترین محصولات (عدد)</div>
            <ResponsiveContainer width="100%" height={Math.max(leastSold.length * 24, 280)}>
              <BarChart data={leastSold} layout="vertical" margin={{ right: 16, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fontFamily: 'Yekan Bakh', direction: 'ltr' }} />
                <Tooltip formatter={(v: any) => [v, 'عدد']} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} fill={LEAST_SOLD_COLOR} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Products table */}
      <div className="analyticsTableCard">
        <div className="analyticsTableHeader">
          <div className="analyticsChartTitle" style={{ margin: 0 }}>جدول همه محصولات</div>
          <button className="analyticsExportBtn" onClick={exportProducts}>⬇ خروجی اکسل</button>
        </div>

        <div style={{ marginBottom: 12, display: 'flex', flexDirection: 'row-reverse', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <input
            className="editProductInput"
            style={{ width: 300, marginBottom: 0 }}
            placeholder="جستجو بر اساس نام یا SKU..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div style={{ display: 'flex', flexDirection: 'row-reverse', gap: 8 }}>
            {([
              { key: 'all', label: 'همه' },
              { key: 'box', label: 'قوطی' },
              { key: 'roll', label: 'رولی' },
            ] as const).map(o => (
              <button
                key={o.key}
                className={`presetBtn${unitFilter === o.key ? ' active' : ''}`}
                onClick={() => setUnitFilter(o.key)}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <table className="alternating-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>#</th>
              <th>عنوان محصول</th>
              <th>SKU</th>
              <th>تعداد فروخته‌شده (عدد)</th>
              <th>تعداد سفارشات</th>
              <th>مجموع درآمد (تومان)</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p, i) => (
              <tr key={p.productId} className={i % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                <td>{i + 1}</td>
                <td style={{ textAlign: 'right' }}>{p.title}</td>
                <td style={{ direction: 'ltr' }}>{p.sku || '—'}</td>
                <td><strong>{p.totalCount}</strong></td>
                <td>{p.orderCount}</td>
                <td>{tools.formatPrice(toToman(p.totalRevenue))}</td>
                <td>
                  <button
                    onClick={() => navigate(`/analytics/products/${p.productId}`, { state: { dateRange } })}
                    style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #14337D', color: '#14337D', background: 'transparent', cursor: 'pointer', fontSize: 12 }}
                  >
                    جزئیات
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#aaa' }}>
                {search ? 'محصولی با این مشخصات یافت نشد' : 'داده‌ای یافت نشد'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

    </AnalyticsLayout>
  );
}
